import { useForm } from '@abyss/web/hooks/useForm';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { Button } from '@abyss/web/ui/Button';
import { DropdownMenu } from '@abyss/web/ui/DropdownMenu';
import { Flex } from '@abyss/web/ui/Flex';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { Heading } from '@abyss/web/ui/Heading';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { PageBody } from '@abyss/web/ui/PageBody';
import { useState } from 'react';

import { useGeoLocationStorage } from '../../hooks/useGeoLocationStorage';
import { DateTime } from '../../utils/DateTime';
import plans from './mockPlans.json';

interface Plan {
  name: string;
  policyNumber: string;
  reciprocityId: string;
  planVariationCode: string;
}

export const GuestPlanPicker = () => {
  const { stateCode } = useGeoLocationStorage();
  const { navigate, getSearchParams } = useRouter();
  const [searchParams] = getSearchParams();
  const lineOfBusiness = searchParams?.get('lob');
  const currentYear =
    DateTime.getCurrentDateTimeWithCentralOffset().getFullYear();
  const [coverageType, setCoverageType] = useState<string>('M');
  const [planName, setPlanName] = useState<string>('Choice Example');
  const [planYear, setPlanYear] = useState<number>(currentYear);

  const selectedPlan: Plan = plans[lineOfBusiness].find(
    (plan) => plan.name === planName
  );

  const form = useForm({
    defaultValues: {
      'dropdown-menu-radios-lob': 'E&I',
      'dropdown-menu-radios-coverage-type': 'M',
      'dropdown-menu-radios-selected-plan': 'Choice Example',
      'dropdown-menu-radios-plan-year': currentYear,
    },
  });

  const buildPortalData = (): any => {
    const { policyNumber, reciprocityId, planVariationCode } =
      selectedPlan || {};
    let coverageEffStartDate,
      coverageEffEndDate = '';
    if (planYear === currentYear) {
      coverageEffStartDate = `${currentYear}-01-01`;
      coverageEffEndDate = `${currentYear}-12-31`;
    } else {
      coverageEffStartDate = `${currentYear + 1}-01-01`;
      coverageEffEndDate = `${currentYear + 1}-12-31`;
    }

    let population = '';
    if (coverageType === 'M') {
      if (lineOfBusiness?.toLocaleLowerCase() === 'c&s') {
        population = 'Medicaid';
      }
    }

    return {
      lineOfBusiness,
      policyNumber,
      reciprocityId,
      planVariationCode,
      population,
      coverageTypeCode: coverageType,
      coverageEffStartDate,
      coverageEffEndDate,
      stateCode,
    };
  };

  return (
    <PageBody>
      <Flex justify="center" style={{ paddingTop: '50px' }}>
        <FormProvider state={form}>
          <Layout.Stack>
            <Heading>Choose your plan</Heading>

            <DropdownMenu
              after={<IconSymbol icon="keyboard_arrow_down" />}
              label={`Coverage Type: ${coverageType}`}
              menuItems={[
                {
                  label: `Coverage Type`,
                  model: 'dropdown-menu-radios-coverage-type',
                  value: coverageType,
                  onChange: setCoverageType,
                  radios: [
                    { label: 'M', value: 'M' },
                    { label: 'BH', value: 'BH' },
                    { label: 'D', value: 'D' },
                    { label: 'V', value: 'V' },
                  ],
                },
              ]}
              outline
            />

            <DropdownMenu
              after={<IconSymbol icon="keyboard_arrow_down" />}
              label={`Selected Plan: ${planName}`}
              menuItems={[
                {
                  label: 'Selected Plan',
                  model: 'dropdown-menu-radios-selected-plan',
                  value: planName,
                  onChange: setPlanName,
                  radios: plans[lineOfBusiness].map((plan) => ({
                    label: plan.name,
                    value: plan.name,
                  })),
                },
              ]}
              outline
            />

            <DropdownMenu
              after={<IconSymbol icon="keyboard_arrow_down" />}
              label={`Plan Year: ${planYear}`}
              menuItems={[
                {
                  label: 'Plan Year',
                  model: 'dropdown-menu-radios-plan-year',
                  value: planYear,
                  onChange: setPlanYear,
                  radios: [
                    {
                      label: `${currentYear}`,
                      value: currentYear.toString(),
                    },
                    {
                      label: `${currentYear + 1}`,
                      value: (currentYear + 1).toString(),
                    },
                  ],
                },
              ]}
              outline
            />

            <Button
              data-testid="guest-plan-picker-submit-button"
              onClick={() =>
                navigate('/browse', {
                  state: buildPortalData(),
                })
              }
            >
              Submit
            </Button>
          </Layout.Stack>
        </FormProvider>
      </Flex>
    </PageBody>
  );
};
